import React, { useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';
import {
  CustomModelClient,
  UserSessionProvider,
  getEnvProp,
  respGridUtil,
  DynamicDCAPageProvider,
  RENDERER,
} from '@marriott/mi-headless-utils';
import { setSubDirectoryPrefix as shopSetSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import {
  OVERVIEW_PAGE_URL,
  SWEEPSTAKES_ENTRY_PAGE_URL,
  constants,
  LCA_CONSTANTS,
  envVarKeys,
  imageDomain,
  imageDomainCN,
} from '@marriott/mi-account-components/constants';
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import { defineComponentMapping } from '../import-components';
import { canUseDOM } from '@marriott/mi-ui-library';
import {
  ACDLanalyticsWrapper,
  BACKGROUND_COLOR_PAGE_ARRAY,
  PageContext,
  ternaryHandler,
} from '@marriott/mi-account-components';
import {
  UpdateDatalayerObj,
  setHWSReservationURL,
  setSubDirectoryPrefix,
  setRitzCarltonUrl,
  correctSubDirectoryPathForHeader,
} from '@marriott/mi-account-components/utils';
import { useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { loadLocale, loadLocaleCurrency, setSessionKey } from '@marriott/mi-book-components';
import path from 'path';

import 'react-loading-skeleton/dist/skeleton.css';
import '@splidejs/splide/css';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
  }
}

if (canUseDOM) window.$ = window.jQuery = require('jquery-slim/dist/jquery.slim.min.js');

const modelClient = new CustomModelClient(useClientEnvVarsStore.getState().envVarsObject['NEXT_PUBLIC_AEM_HOST']);

if (useClientEnvVarsStore.getState().envVarsObject['NODE_ENV'] !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props) {
  const router = useRouter();
  const { Component, pageProps } = props;
  const {
    model,
    resolvedUrl,
    isAuthorMode,
    sessionData,
    sessionTimeOutPage = false,
    isEAA = false,
    isMobileAuthFeatureEnabled = false,
    isNonChinaMobileEnabled = false,
    sweepstakesPreprocessorResponse = {},
    isResetTokenValid,
    apolloEnvVars,
    userState,
    isDTT,
    isPointsTransferSuccess = false,
    cnEnvVars,
    NEXT_GLOBAL_ASSET_PREFIX,
    enrollPrefillResponse = {},
    propertyDttDetails,
    featureFlagLogo,
    dataLayer,
    GOOGLE_MAP_API_KEY,
    EXPERIENCE_SEGMENT,
    featureFlags,
    serverTimestamp,
  } = pageProps;

  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); //To deep clone the model
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  if (setPageModel) {
    setPageModel(respGridModelClone);
  }

  const isDtt = propertyDttDetails ? propertyDttDetails?.dtt && isDTT : isDTT;
  const isAcrs = propertyDttDetails ? propertyDttDetails?.acrs : false;
  const updatedPageProps = { ...pageProps, isDTT: isDtt, isACRS: isAcrs };

  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };
  const serverENV = pageProps?.serverENV ? JSON.parse(pageProps?.serverENV) : {};
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  let envVars = apolloEnvVars ? apolloEnvVars : {};
  const urlsToClearSessionData = envVars?.URLS_TO_CLEAR_SESSION_DATA;
  envVars['ACCEPT_LANGUAGE'] = pageProps.currentLocale;
  envVars['TRACKING_PAGE_NAME'] = model?.trackingPageName;
  envVars['GOOGLE_MAP_API_KEY'] = GOOGLE_MAP_API_KEY || '';
  envVars['UXL_IMAGE_CACHE_DOMAIN'] =
    pageProps.currentLocale === constants.LOCALE_KEY_CHINA ? imageDomainCN : imageDomain;
  constants?.NEXT_PUBLIC_ENV_KEYS?.map(envKey => (envVars[envKey] = serverENV[envKey]));
  const isAcdlEnabledForCurrentPage = Boolean(model?.IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL);
  envVars = {
    ...envVars,
    NEXT_PUBLIC_ENABLE_WEBSDK:
      String(ternaryHandler(getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK))) === 'true' &&
      String(model?.enableWebSDK) === 'true' &&
      isAcdlEnabledForCurrentPage,
    NEXT_PUBLIC_ENABLE_DCA_V2: String(ternaryHandler(getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2))),
    SKIP_MVP_OFFERS: String(getEnvProp(envVarKeys.SKIP_MVP_OFFERS)),
  };
  setEnvVars?.({ ...cnEnvVars, ...envVars });
  let subDirectoryPrefix = '';
  if (model) {
    subDirectoryPrefix = model.subDirectoryPrefix ? model.subDirectoryPrefix : '';
    setSubDirectoryPrefix(subDirectoryPrefix);
    setHWSReservationURL(model?.hwsURL || '');
    setRitzCarltonUrl(model?.rcURL || '');
    shopSetSubDirectoryPrefix(subDirectoryPrefix);
  }
  defineComponentMapping(allowedComponents, resolvedUrl, isAuthorMode, isDtt, pageProps, featureFlagLogo);

  const pageContext = useMemo(() => {
    /** attache memo value */
    return {
      /**use only to read the values */
      sessionData: sessionData,
      sessionTimedOutPage: sessionTimeOutPage,
      isEAASignIn: isEAA,
      serverENV: pageProps?.serverENV,
      currentLocale: pageProps.currentLocale, // to get current locale
      isMobileAuthFeatureEnabled: isMobileAuthFeatureEnabled,
      isNonChinaMobileEnabled: isNonChinaMobileEnabled,
      isOverViewPage: pageProps?.requestUri?.toLowerCase() === OVERVIEW_PAGE_URL?.toLowerCase(),
      uxlErrorMessage:
        model?.uxlFailureErrorMessage ??
        '<strong>We’re having trouble fetching some data.</strong> Please try again in a moment.',
      memberStatusList: model?.memberStatusList,
      sweepstakesPreprocessorResponse: sweepstakesPreprocessorResponse,
      rememberMe: pageProps?.rememberMe,
      enrollAndLinkStatus: pageProps.enrollAndLinkStatus ? pageProps.enrollAndLinkStatus : null,
      isResetTokenValid: isResetTokenValid,
      userState: userState,
      isDTT: isDTT,
      nuaDetails: pageProps.nuaDetails ? pageProps.nuaDetails : null,
      enableTimer: model?.timerFlag,
      isPointsTransferSuccess: isPointsTransferSuccess,
      localeBasedNameSwapConfig: model?.localeBasedNameSwapConfig,
      rtlEnable: model?.rtlEnable,
      enrollPrefillResponse: enrollPrefillResponse,
      featureFlags: featureFlags,
      serverTimestamp: serverTimestamp,
      /**
       * pass some datalayer property for server side processing
       */
      datalayer: {
        /**
         * red the geo location country to  render the components
         */
        currentLocCountry: dataLayer?.data?.[0]?.browser_akamai_loc_country,
      },
    };
  }, [sessionTimeOutPage]);

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (pageProps?.model?.rtlEnable == 'true'
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.min.css`
          : `global-styles/${globalStylesVersion}/marriot.global.min.css`);
      document.head.appendChild(globalStyle);

      const { asPath } = router;
      const path = asPath.split('/').filter(Boolean);
      const lastSegment = path[path.length - 1];
      const brandCodeMatch = lastSegment?.match(/brandCode=([^&]+)/);
      const brandCode = brandCodeMatch ? brandCodeMatch[1] : '';

      if (brandCode) {
        const brandLink = document.createElement('link');
        brandLink.rel = 'stylesheet';
        brandLink.href = assetUrl + `brand-framework/brand-config.${brandCode}.css`;
        document.head.appendChild(brandLink);
      }
    }
    const isPageFound =
      BACKGROUND_COLOR_PAGE_ARRAY.includes(pageProps?.requestUri) ||
      pageProps?.requestUri?.includes(SWEEPSTAKES_ENTRY_PAGE_URL);
    if (pageProps?.requestUri && isPageFound) {
      document.body.className += ' color-scheme3';
    }
    if (
      String(getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK)) !== 'true' ||
      String(model?.enableWebSDK) !== 'true' ||
      !isAcdlEnabledForCurrentPage
    ) {
      const dataLayerObj = window.dataLayer ?? {};
      UpdateDatalayerObj(dataLayerObj);
    }
    // updated header nav items with subdirectory prefix
    correctSubDirectoryPathForHeader();
    // This is added as a part of NUA globalization as we are reusing book components. We are getting these labels from page level model which are required on below methods that are used for updating the locale, date formatting & currency symbol.
    if (model && pageProps.nuaDetails) {
      const {
        language = '',
        dateOrdinal = '',
        dateFormat = '',
        subDirectoryPrefix = '',
        localeCurrencySymbol = '',
      } = model;
      loadLocaleCurrency(localeCurrencySymbol);
      setSessionKey('SUBDIRECTORYCONTEXTPATH', subDirectoryPrefix);
      loadLocale(language, dateOrdinal, dateFormat);
    }

    const isLocaleClearURL = urlsToClearSessionData?.includes(pageProps?.requestUri);
    if (isLocaleClearURL) {
      localStorage?.removeItem('mi-session-store');
    }
  }, []);

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });
  return (
    <UserSessionProvider pageProps={updatedPageProps} renderer={RENDERER.ACCOUNT} hasOwnDataLayer={true}>
      <PageContext.Provider value={pageContext}>
        <ACDLanalyticsWrapper />
        <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable == 'true' ? [rtlPluginTs] : []}>
          <DynamicDCAPageProvider Component={Component} pageProps={updatedPageProps} />
        </StyleSheetManager>
      </PageContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
